// Monthly streaming report for August 2026.
import { defineDocument, em, link, list, p, section, table, text, } from '../../schema';
export const report202608TopPartyGamesAndStreamersDocument = defineDocument({
    id: 'report:2026-08-top-party-games-and-streamers',
    kind: 'report',
    slug: '2026-08-top-party-games-and-streamers',
    route: '/party-central/reports/2026-08-top-party-games-and-streamers',
    seo: {
        title: 'August 2026: Top Party Games & Streamers on Twitch',
        description: 'Data-driven analysis of party game streaming on Twitch for August 2026. Party Score rankings, top games, streamers, language breakdowns, and new releases.',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Party Central Report',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'August 2026',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Top Party Games & Streamers on Twitch',
            },
        ],
        background: 'gradient-card',
        backgroundAccentFamily: 'lime',
        showDescription: true,
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-09-23T00:00:00.000Z'),
        updatedAt: new Date('2026-09-23T00:00:00.000Z'),
        reportMonth: '2026-08',
        dataRange: {
            start: '2026-08-01',
            end: '2026-08-31',
        },
    },
    aside: {
        showToc: true,
    },
    sections: [
        section({
            id: 'executive-summary',
            title: 'Executive Summary',
            collapsible: false,
            blocks: [
                p([
                    text('August recorded 193,339 party-game streams from 84,239 streamers in 34 languages, 28% fewer streams than July. '),
                    em('MECCHA CHAMELEON'),
                    text(' accounts for the entire drop: it lost about 77,000 streams as its July surge faded. Streams for all other games combined were flat, but that masks two opposing forces. '),
                    em('The Choicer Voicer'),
                    text(', a voice-impression party game, grew from almost nothing to 4,705 streamers, while streams for the remaining games fell about 3%.'),
                ]),
                p([
                    text('Back on top of the Party Score rankings was '),
                    em('nanos world'),
                    text(' because of Zenkai RP, a French Naruto roleplay server built on the sandbox game. '),
                    em('Diablo II: Resurrected'),
                    text(' came second after its new ladder season opened on August 21. '),
                    em('Pizza House Simulator'),
                    text(' was the strongest new release, with 878 streamers.'),
                ]),
                p([
                    text('The top streamer by Party Score was '),
                    link('https://twitch.tv/nohoho12', 'のほほ'),
                    text(', a Japanese '),
                    em('Super Smash Bros. Ultimate'),
                    text(' player who runs the NOHOHO Off tournament series. '),
                    link('https://twitch.tv/x33n', 'X33N'),
                    text(' and '),
                    link('https://twitch.tv/kept_varrel', 'kept_VARREL'),
                    text(' matched the top Party Score of 98, but のほほ ranked first on a larger average audience.'),
                ]),
            ],
        }),
        section({
            id: 'party-score-rankings',
            title: 'Party Score Rankings',
            collapsible: true,
            sections: [
                section({
                    id: 'party-score-rankings__games-by-party-score',
                    title: 'Games by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            em('nanos world'),
                            text(' returned to first place with a score of 96, matching its April high. Nearly all of its August activity came from one French Naruto roleplay server, '),
                            link('https://zenkai-rp.com/qui-sommes-nous', 'Zenkai RP'),
                            text(". Zenkai appeared in 90% of the game's stream titles, and French channels made 95% of its streams. Audiences grew in mid-August, from about 35 average viewers before August 14 to about 104 after. "),
                            link('https://twitch.tv/mehditdonc', 'MehDitDonc'),
                            text(' drove much of that rise with a near-daily "Épisode" series that began on August 13, and drew 40% of the game\'s viewers for the month.'),
                        ]),
                        p([
                            em('Diablo II: Resurrected'),
                            text(', an action RPG, came second after '),
                            link('https://news.blizzard.com/en-us/article/24296140/diablo-ii-resurrected-ladder-season-15-coming-soon', 'Ladder Season 15'),
                            text(" opened on August 21. A ladder reset sends players back to fresh characters, and streamers came back for it: daily streams jumped from an average of about 173 to 818 on August 22. The last 10 days accounted for more than half of the month's streams, and the game finished 63% above July."),
                        ]),
                        p([
                            text('Most '),
                            em('Pinball'),
                            text(" streams came from real arcades and leagues broadcasting from their venues, including the 2026 Northwest Pinball Championships and the Tarraco Pinball Tournament 2026. That base is loyal: two-thirds of July's "),
                            em('Pinball'),
                            text(' streamers came back in August, the highest return rate among the games we checked. '),
                            link('https://twitch.tv/foxcitiespinball', 'Foxcitiespinball'),
                            text(" drew the biggest audiences, with a third of the game's viewers."),
                        ]),
                        p([
                            text('Most of the audience for '),
                            em('Super Mario Party Jamboree'),
                            text(' came from one session. On August 24, '),
                            link('https://twitch.tv/squeex', 'Squeex'),
                            text(', '),
                            link('https://twitch.tv/valkyrae', 'Valkyrae'),
                            text(', '),
                            link('https://twitch.tv/fuslie', 'Fuslie'),
                            text(', and '),
                            link('https://twitch.tv/foolish', 'Foolish'),
                            text(" played together in person and each streamed it. Those streams drew 65% of the game's August viewers. Its other 1,235 streams averaged about 15 viewers."),
                        ]),
                        p([
                            text('Check out all games by Party Score in the '),
                            link('/party-central?measure=gamePartyScore&x=game&grain=&from=2026-08-01T00%3A00%3A00.000Z&to=2026-09-01T00%3A00%3A00.000Z', 'data explorer'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Party Score',
                                'Streams',
                                'Streamers',
                                'Stickiness',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'nanos world', '96', '3,152', '215', '14.7', '78'],
                                [
                                    '2',
                                    'Diablo II: Resurrected',
                                    '93',
                                    '8,320',
                                    '1,301',
                                    '6.4',
                                    '21',
                                ],
                                [
                                    '3',
                                    'Super Smash Bros. Ultimate',
                                    '92',
                                    '11,817',
                                    '3,497',
                                    '3.4',
                                    '20',
                                ],
                                ['4', 'Words On Stream', '92', '7,161', '594', '12.1', '16'],
                                [
                                    '5',
                                    'Ragnarok Origin: Classic',
                                    '92',
                                    '1,562',
                                    '201',
                                    '7.8',
                                    '21',
                                ],
                                [
                                    '6',
                                    'Just Dance 2026 Edition',
                                    '92',
                                    '495',
                                    '197',
                                    '2.5',
                                    '47',
                                ],
                                ['7', 'Pinball', '92', '674', '160', '4.2', '26'],
                                ['8', 'Gartic on Stream', '92', '266', '43', '6.2', '55'],
                                [
                                    '9',
                                    'Jackbox Party Packs',
                                    '91',
                                    '3,991',
                                    '1,993',
                                    '2.0',
                                    '31',
                                ],
                                [
                                    '10',
                                    'Super Mario Party Jamboree',
                                    '91',
                                    '1,243',
                                    '620',
                                    '2.0',
                                    '44',
                                ],
                                ['11', 'Sponge Break', '91', '249', '64', '3.9', '34'],
                                [
                                    '12',
                                    'Mario Kart World',
                                    '90',
                                    '4,878',
                                    '1,915',
                                    '2.5',
                                    '18',
                                ],
                                ['13', 'Pokémon Stadium', '90', '276', '102', '2.7', '30'],
                                ['14', 'Lycans', '90', '163', '72', '2.3', '256'],
                                [
                                    '15',
                                    'The Legend of Heroes: Trails of Cold Steel III',
                                    '90',
                                    '303',
                                    '37',
                                    '8.2',
                                    '28',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'party-score-rankings__streamers-by-party-score',
                    title: 'Streamers by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/nohoho12', 'のほほ'),
                            text(', '),
                            link('https://twitch.tv/x33n', 'X33N'),
                            text(', and '),
                            link('https://twitch.tv/kept_varrel', 'kept_VARREL'),
                            text(' tied for the top score of 98. のほほ and kept_VARREL are both Japanese '),
                            em('Super Smash Bros. Ultimate'),
                            text(' players. のほほ is the channel of '),
                            link('https://www.ssbwiki.com/Smasher:NOHOHO', 'NOHOHO'),
                            text(", a Ness and Lucas player who runs the NOHOHO Off tournament series, and 22 of its 27 streams were Smash. kept_VARREL played Smash in 34 of 40 streams. Both were already in July's top 15 and climbed as several July leaders dropped out."),
                        ]),
                        p([
                            link('https://twitch.tv/vgbootcamp', 'VGBootCamp'),
                            text(" entered at fifth by broadcasting August's big Smash tournaments: "),
                            link('https://www.ssbwiki.com/Tournament:Supernova_2026', 'Supernova 2026'),
                            text(' in Baltimore, where finals day drew more than 11,000 viewers, and '),
                            link('https://www.ssbwiki.com/Tournament:CEO_2026', 'CEO 2026'),
                            text(' in Orlando on August 14 to 16. The channel carried a quarter of all '),
                            em('Super Smash Bros. Ultimate'),
                            text(' viewers for the month.'),
                        ]),
                        p([
                            text("Nearly all of X33N's streams, 109 of 121, were "),
                            em('Words On Stream'),
                            text('. '),
                            link('https://twitch.tv/ponce', 'Ponce'),
                            text(' ranked fourth with only 14 streams, all in French, across '),
                            em('Lycans'),
                            text(', '),
                            em('Maitre des Fleurs'),
                            text(', and '),
                            em('An Eggstremely Hard Game'),
                            text('. '),
                            link('https://twitch.tv/paache_', 'paache_'),
                            text(' took the same route to 13th: nine streams averaging about 2,100 viewers. The Spanish channel runs a long series called "Reviviendo la Wii" (reliving the Wii), now past episode 440, and spent August chasing platinum medals in '),
                            em('Wii Sports'),
                            text(' and climbing in '),
                            em('Mario Kart World'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Party Score',
                                'Observations',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/nohoho12', 'のほほ')],
                                    '98',
                                    '27',
                                    '279',
                                    '5',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/x33n', 'X33N')],
                                    '98',
                                    '121',
                                    '170',
                                    '4',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/kept_varrel', 'kept_VARREL')],
                                    '98',
                                    '40',
                                    '144',
                                    '4',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/ponce', 'Ponce')],
                                    '97',
                                    '14',
                                    '3,586',
                                    '3',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/vgbootcamp', 'VGBootCamp')],
                                    '97',
                                    '34',
                                    '1,732',
                                    '2',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/karacorvus', 'KaraCorvus')],
                                    '97',
                                    '14',
                                    '325',
                                    '4',
                                ],
                                [
                                    '7',
                                    [
                                        link('https://twitch.tv/francesconigris', 'francesconigris'),
                                    ],
                                    '97',
                                    '18',
                                    '157',
                                    '4',
                                ],
                                [
                                    '8',
                                    [
                                        link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                    ],
                                    '97',
                                    '72',
                                    '86',
                                    '8',
                                ],
                                [
                                    '9',
                                    [
                                        link('https://twitch.tv/larryfishburger', 'LarryFishburger'),
                                    ],
                                    '96',
                                    '10',
                                    '280',
                                    '4',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/zarbex_tv', 'zarbex_TV')],
                                    '96',
                                    '12',
                                    '230',
                                    '7',
                                ],
                                [
                                    '11',
                                    [link('https://twitch.tv/konti1337', 'Konti1337')],
                                    '96',
                                    '11',
                                    '186',
                                    '7',
                                ],
                                [
                                    '12',
                                    [link('https://twitch.tv/vanilian', 'Vanilian')],
                                    '96',
                                    '22',
                                    '93',
                                    '3',
                                ],
                                [
                                    '13',
                                    [link('https://twitch.tv/paache_', 'paache_')],
                                    '95',
                                    '9',
                                    '2,127',
                                    '3',
                                ],
                                [
                                    '14',
                                    [link('https://twitch.tv/zeroyalviking', 'ZeRoyalViking')],
                                    '95',
                                    '8',
                                    '356',
                                    '7',
                                ],
                                [
                                    '15',
                                    [link('https://twitch.tv/mang0', 'mang0')],
                                    '95',
                                    '10',
                                    '330',
                                    '3',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'global-overview',
            title: 'Global Overview',
            collapsible: true,
            sections: [
                section({
                    id: 'global-overview__games-with-most-streams',
                    title: 'Games with Most Streams',
                    collapsible: true,
                    blocks: [
                        p([
                            em('MECCHA CHAMELEON'),
                            text(' stayed first but lost three-quarters of its July streams. Only 11% of its July streamers came back, and 80% of its August streamers played it just once. It '),
                            link('/party-central?from=2026-08-01T00%3A00%3A00.000Z&to=2026-09-01T00%3A00%3A00.000Z&game=1820839024', 'kept falling'),
                            text(' through the month, from about 1,135 streams a day in the first week to about 500 in the last. Even at 500 a day, it outpaced second-place '),
                            em('Among Us'),
                            text(', which averaged about 450. '),
                            em('The Choicer Voicer'),
                            text(' entered at eighth, up massively from only 71 streams in July.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'MECCHA CHAMELEON', '23,798', '17,360', '23'],
                                ['2', 'Among Us', '13,930', '8,107', '33'],
                                ['3', 'Fall Guys', '12,744', '6,171', '11'],
                                ['4', 'Super Smash Bros. Ultimate', '11,817', '3,497', '20'],
                                ['5', 'Diablo II: Resurrected', '8,320', '1,301', '21'],
                                ['6', 'Words On Stream', '7,161', '594', '16'],
                                ['7', 'Lethal Company', '6,520', '3,637', '9'],
                                ['8', 'The Choicer Voicer', '5,693', '4,705', '94'],
                                ['9', 'Gartic Phone', '5,356', '3,013', '19'],
                                ['10', 'Mario Kart World', '4,878', '1,915', '18'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-biggest-audiences',
                    title: 'Games with Biggest Audiences',
                    description: 'Minimum 10 streams',
                    collapsible: true,
                    blocks: [
                        p([
                            text('At least half of the top 10 came down to one creator or one evening. '),
                            link('https://store.steampowered.com/app/1521810/Salamander_County_Public_Television/', [em('Salamander County Public Television')]),
                            text(', a single-player collection of narrative minigames, owes its place to one '),
                            link('https://twitch.tv/joe_bartolozzi', 'Joe_Bartolozzi'),
                            text(' stream. '),
                            link('https://twitch.tv/ponce', 'Ponce'),
                            text(' carried '),
                            em('Maitre des Fleurs'),
                            text(' for the third month in a row, and '),
                            link('https://twitch.tv/northernlion', 'Northernlion'),
                            text(' and '),
                            link('https://twitch.tv/mobilmobil', '老皮'),
                            text(' did the same for '),
                            em('We, Junk Artists'),
                            text(' and '),
                            em('One Last Hand'),
                            text('. The average of 177 viewers for '),
                            em('skribbl.io'),
                            text(' was boosted by a single game night on August 8 with '),
                            link('https://twitch.tv/jynxzi', 'Jynxzi'),
                            text(', '),
                            link('https://twitch.tv/agent00', 'Agent00'),
                            text(', '),
                            link('https://twitch.tv/im_dontai', 'Dontai'),
                            text(', and Joe_Bartolozzi.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Avg Viewers',
                                'Streams',
                                'Streamers',
                            ],
                            rows: [
                                [
                                    '1',
                                    'Salamander County Public Television',
                                    '1,642',
                                    '13',
                                    '11',
                                ],
                                ['2', 'Maitre des Fleurs', '1,290', '17', '7'],
                                ['3', 'One Last Hand', '442', '10', '9'],
                                ['4', 'We, Junk Artists', '337', '45', '43'],
                                ['5', 'Lycans', '256', '163', '72'],
                                ['6', 'Deduce Together', '239', '18', '13'],
                                ['7', 'Bow-wow Battle', '193', '11', '11'],
                                ['8', 'Spider Roulette', '187', '17', '15'],
                                ['9', 'skribbl.io', '177', '397', '340'],
                                ['10', 'Super Monkey Ball', '165', '18', '12'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-streamed-most-widely',
                    title: 'Games Streamed Most Widely',
                    collapsible: true,
                    blocks: [
                        p([
                            em('The Choicer Voicer'),
                            text(' made the biggest jump, from 65 streamers in July to 4,705 in August. It is an early-access '),
                            link('https://yeahmaybe.itch.io/the-choicer-voicer', 'voice-impression party game'),
                            text(' from YeahMaybe, with a mode that lets Twitch chat vote. Big creators picked it up one after another, starting with '),
                            link('https://twitch.tv/krokoboss', 'krokoboss'),
                            text(' on August 6 and followed over the next three weeks by '),
                            link('https://twitch.tv/vanillamace', 'VanillaMace'),
                            text(', '),
                            link('https://twitch.tv/rdcgaming', 'RDCgaming'),
                            text(', '),
                            link('https://twitch.tv/cellbit', 'Cellbit'),
                            text(', '),
                            link('https://twitch.tv/papaplatte', 'Papaplatte'),
                            text(', '),
                            link('https://twitch.tv/jynxzi', 'Jynxzi'),
                            text(', '),
                            link('https://twitch.tv/xqc', 'xQc'),
                            text(', '),
                            link('https://twitch.tv/northernlion', 'Northernlion'),
                            text(', and '),
                            link('https://twitch.tv/vinesauce', 'Vinesauce'),
                            text('. The game drew nearly as many total viewers as '),
                            em('MECCHA CHAMELEON'),
                            text(' from less than a quarter of the streams. Most streamers played it only once, so it scores low on stickiness and misses the Party Score top 15.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Streamers', 'Streams'],
                            rows: [
                                ['1', 'MECCHA CHAMELEON', '17,360', '23,798'],
                                ['2', 'Among Us', '8,107', '13,930'],
                                ['3', 'Fall Guys', '6,171', '12,744'],
                                ['4', 'The Choicer Voicer', '4,705', '5,693'],
                                ['5', 'Lethal Company', '3,637', '6,520'],
                                ['6', 'Super Smash Bros. Ultimate', '3,497', '11,817'],
                                ['7', 'Gartic Phone', '3,013', '5,356'],
                                ['8', 'Super Battle Golf', '2,739', '3,511'],
                                ['9', 'Golf With Your Friends', '2,038', '2,959'],
                                ['10', 'Jackbox Party Packs', '1,993', '3,991'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-stickiest-streamers',
                    title: 'Games with Stickiest Streamers',
                    description: 'Minimum 20 unique streamers',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Stickiness is the average number of tracked streams per streamer. '),
                            em('nanos world'),
                            text(' led again on a returning community: two-thirds of its July streamers came back. '),
                            em('Diablo II: Resurrected'),
                            text(' got stickier as the new ladder season pulled players back, rising from 5.7 streams per streamer in July to 6.4, its highest since March. '),
                            em('Drawful 2'),
                            text(' is on the list because of one channel: '),
                            link('https://twitch.tv/cpt_meticulous', 'cpt_meticulous'),
                            text(' streamed it 210 times.'),
                        ]),
                        p([
                            em('Ragnarok Origin: Classic'),
                            text(' is an MMORPG that '),
                            link('https://www.globenewswire.com/news-release/2026/07/24/3332739/0/en/Ragnarok-Origin-Classic-Officially-Launched-in-North-Central-and-South-America-on-July-23-2026.html', 'launched in the Americas'),
                            text(" on July 23. Only a third of July's players returned in August, but that smaller group kept coming back, which raised the game's stickiness score."),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Stickiness', 'Streams', 'Streamers'],
                            rows: [
                                ['1', 'nanos world', '14.7', '3,152', '215'],
                                ['2', 'Words On Stream', '12.1', '7,161', '594'],
                                ['3', 'Musical Vibes', '11.4', '251', '22'],
                                ['4', 'Drawful 2', '11.4', '229', '20'],
                                ['5', 'Ragnarok X: Next Generation', '9.7', '214', '22'],
                                [
                                    '6',
                                    'The Legend of Heroes: Trails of Cold Steel III',
                                    '8.2',
                                    '303',
                                    '37',
                                ],
                                ['7', 'Ragnarok Origin: Classic', '7.8', '1,562', '201'],
                                [
                                    '8',
                                    'The Legend of Heroes: Trails of Cold Steel II',
                                    '7.4',
                                    '229',
                                    '31',
                                ],
                                [
                                    '9',
                                    'DEAD OR ALIVE Xtreme Venus Vacation',
                                    '6.6',
                                    '152',
                                    '23',
                                ],
                                ['10', 'Diablo II: Resurrected', '6.4', '8,320', '1,301'],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'new-releases',
            title: 'New Releases',
            collapsible: true,
            sections: [
                section({
                    id: 'new-releases__new-releases-seen-on-twitch',
                    title: 'New Releases Seen on Twitch',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://store.steampowered.com/app/4132330/Pizza_House_Simulator/', [em('Pizza House Simulator')]),
                            text(', an online co-op game for up to six players, was the strongest new release we have tracked since April apart from '),
                            em('MECCHA CHAMELEON'),
                            text('. German creator '),
                            link('https://twitch.tv/trymacs', 'Trymacs'),
                            text(' brought 44% of its viewers across three streams, including an August 30 co-op session with '),
                            link('https://twitch.tv/rumathra', 'Rumathra'),
                            text(', '),
                            link('https://twitch.tv/gtimetv', 'GtimeTV'),
                            text(', and '),
                            link('https://twitch.tv/cezzy', 'Cezzy'),
                            text('. In the final 10 days, Chinese and Japanese creators such as '),
                            link('https://twitch.tv/mobilmobil', '老皮'),
                            text(' and '),
                            link('https://twitch.tv/gutitubo', 'gutitubo'),
                            text(' added streams of 3,000 to 5,400 viewers.'),
                        ]),
                        p([
                            link('https://store.steampowered.com/app/3470550/We_Junk_Artists/', [em('We, Junk Artists')]),
                            text(' is a collage-and-guess game for up to six players: instead of drawing, players build pictures from piles of junk and others guess what they made. Chinese publisher Spiral Up Games released it, and 26 of its 43 streamers were Chinese-language creators, but '),
                            link('https://twitch.tv/northernlion', 'Northernlion'),
                            text(' brought most of its audience.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Game',
                                'Released',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['Pizza House Simulator', 'Aug 19', '1,337', '878', '82'],
                                ['Keep It Up!', 'Aug 1', '139', '131', '14'],
                                ['We, Junk Artists', 'Aug 7', '45', '43', '337'],
                                ['Dino Party', 'Aug 14', '43', '42', '113'],
                                ['Bottle Cracks', 'Aug 10', '26', '22', '15'],
                                ["Din's Champion", 'Aug 24', '23', '14', '9'],
                                ['Magic Cards Duel', 'Aug 12', '14', '10', '7'],
                                ['Smack Talk', 'Aug 27', '11', '11', '39'],
                                ['One Last Hand', 'Aug 14', '10', '9', '442'],
                                ['Choppa: Rescue Rivals', 'Aug 13', '9', '7', '5'],
                                ['Eat the Rich', 'Aug 28', '8', '6', '25'],
                                [
                                    'Grizzy & the Lemmings: Crazy Party',
                                    'Aug 7',
                                    '3',
                                    '3',
                                    '17',
                                ],
                                ['Prison Gambit', 'Aug 7', '3', '3', '212'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'new-releases__other-new-releases',
                    title: 'Other New Releases',
                    collapsible: true,
                    blocks: [
                        p('Our tracker did not record Twitch activity for 38 other party games released in August. They are listed below by release date.'),
                        list({
                            items: [
                                'Jobby (Aug 1)',
                                'Mischief House (Aug 1)',
                                'Rootlify (Aug 1)',
                                'FrankenSketch (Aug 2)',
                                'Buzzin (Aug 4)',
                                'Super Friends Party (Aug 4)',
                                'Dark Witch Match! (Aug 5)',
                                'Blast Off! Summer Showdown (Aug 6)',
                                'Limbot (Aug 6)',
                                'Loco! (Aug 6)',
                                'Route-16 Collection (Aug 6)',
                                'Route-16: R (Aug 6)',
                                'Smugg.io (Aug 11)',
                                'Sneaky Clean (Aug 11)',
                                'Warhounds (Aug 11)',
                                'Pro Jank Footy (Aug 12)',
                                'Rocket Sideswipe (Aug 13)',
                                'Stack Mates (Aug 13)',
                                'Rhythm Racer (Aug 14)',
                                'The Hidden Camp (Aug 19)',
                                'Deep Blue Sushi (Aug 20)',
                                'PokeRank (Aug 20)',
                                'Spot the Difference Ukiyo-e: Fifty-Three Stations of the Toukaidou (Aug 20)',
                                'C.U.B.E (Aug 21)',
                                'Fear of Faith (Aug 21)',
                                'Galumb (Aug 21)',
                                'Trial by Chat (Aug 24)',
                                'Gold Coast Gambits: Boca Raton & Palm Beach (Aug 25)',
                                'Hide and Moo! (Aug 25)',
                                'Hide and Fuse (Aug 26)',
                                'The Grand Slamboree (Aug 26)',
                                'Cursed Objects: Find and Seal (Aug 27)',
                                'Hidden Photo Mysteries: The Truth Gets Scary (Aug 27)',
                                'Mimic Party (Aug 27)',
                                'Mythical Creature Personality Quiz: 16 Types (Aug 27)',
                                'Can I Take Your Order (Aug 28)',
                                'Peekablock (Aug 28)',
                                'RunOut: Hothead Paws (Aug 28)',
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streamer-spotlight',
            title: 'Streamer Spotlight',
            collapsible: true,
            sections: [
                section({
                    id: 'streamer-spotlight__streamers-with-most-activity',
                    title: 'Streamers with Most Activity',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            text('The top seven channels were live nearly every time we checked Twitch, at least 200 of 210 times, and each streamed only one game. '),
                            link('https://twitch.tv/telinha', 'Telinha'),
                            text(' streamed '),
                            em('Fall Guys'),
                            text(' in Portuguese and drew 13% of all '),
                            em('Fall Guys'),
                            text(' viewers. '),
                            link('https://twitch.tv/mrllamavod', 'mrllamavod'),
                            text(' looped '),
                            link('https://twitch.tv/mrllamasc', 'MrLlamaSC'),
                            text("'s "),
                            em('Diablo II: Resurrected'),
                            text(' videos around the clock. '),
                            link('https://twitch.tv/suzyqpid', 'suzyqpid'),
                            text(', '),
                            link('https://twitch.tv/thesptwitch', 'TheSpTwitch'),
                            text(', and '),
                            link('https://twitch.tv/tygerladi', 'TygerLadi'),
                            text(' streamed '),
                            em('Words On Stream'),
                            text(', '),
                            link('https://twitch.tv/jackboxplay', 'jackboxplay'),
                            text(' streamed '),
                            em('Jackbox Party Packs'),
                            text(' in Russian, and '),
                            link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                            text(' streamed '),
                            em('Super Smash Bros. Ultimate'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Observations',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/telinha', 'Telinha')],
                                    '210',
                                    '91',
                                    '1',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                    '210',
                                    '87',
                                    '1',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                    '210',
                                    '11',
                                    '1',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                    '208',
                                    '34',
                                    '1',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/jackboxplay', 'jackboxplay')],
                                    '206',
                                    '46',
                                    '1',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                    '203',
                                    '20',
                                    '1',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate')],
                                    '200',
                                    '29',
                                    '1',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/scufflestv', 'ScufflesTV')],
                                    '133',
                                    '15',
                                    '1',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/splatpoyo', 'SplatPoyo')],
                                    '123',
                                    '11',
                                    '1',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/x33n', 'X33N')],
                                    '121',
                                    '170',
                                    '4',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__streamers-with-biggest-audiences',
                    title: 'Streamers with Biggest Audiences',
                    description: 'Minimum 2 observations',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Half of the streamers on the list are new to it since April. '),
                            link('https://twitch.tv/joe_bartolozzi', 'Joe_Bartolozzi'),
                            text(' took third place after a '),
                            em('skribbl.io'),
                            text(' game night, '),
                            link('https://twitch.tv/squeex', 'Squeex'),
                            text(' entered with a successful in-person '),
                            em('Mario Party'),
                            text(' session, and '),
                            link('https://twitch.tv/angryginge13', 'angryginge13'),
                            text(' showed great chemistry playing '),
                            em('MECCHA CHAMELEON'),
                            text(' with their best friends. '),
                            link('https://twitch.tv/vinesauce', 'Vinesauce'),
                            text(' and Spanish streamer '),
                            link('https://twitch.tv/aldo_geo', 'aldo_geo'),
                            text(' both tried '),
                            em('The Choicer Voicer'),
                            text('. Among the regulars, '),
                            link('https://twitch.tv/eliasn97', 'eliasn97'),
                            text(' made the list for a fifth straight month and drew 35% of all '),
                            em('Among Us'),
                            text(' viewers in August.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Avg Viewers',
                                'Observations',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/jynxzi', 'Jynxzi')],
                                    '36,811',
                                    '2',
                                    '2',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                    '33,139',
                                    '7',
                                    '4',
                                ],
                                [
                                    '3',
                                    [
                                        link('https://twitch.tv/joe_bartolozzi', 'Joe_Bartolozzi'),
                                    ],
                                    '20,772',
                                    '2',
                                    '2',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/xqc', 'xQc')],
                                    '19,964',
                                    '3',
                                    '3',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/trymacs', 'Trymacs')],
                                    '16,223',
                                    '3',
                                    '1',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/northernlion', 'Northernlion')],
                                    '11,940',
                                    '4',
                                    '4',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/angryginge13', 'angryginge13')],
                                    '7,333',
                                    '2',
                                    '2',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/squeex', 'Squeex')],
                                    '6,722',
                                    '2',
                                    '1',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/vinesauce', 'Vinesauce')],
                                    '6,098',
                                    '2',
                                    '2',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/aldo_geo', 'aldo_geo')],
                                    '5,923',
                                    '4',
                                    '4',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__streamers-with-most-versatility',
                    title: 'Streamers with Most Versatility',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/partychads', 'PARTYCHADS'),
                            text(' led with 17 games, mostly its usual retro '),
                            em('Mario Party'),
                            text(' titles. '),
                            link('https://twitch.tv/alexfrets', 'AlexFrets'),
                            text(' ran "Guitar Hero Gauntlet 2", playing a different '),
                            em('Guitar Hero'),
                            text(' game each day. Along the way they played '),
                            em('DJ Hero'),
                            text(' and '),
                            em('Band Hero'),
                            text(', finishing with '),
                            em('Warriors of Rock'),
                            text(' on day 11 of the gauntlet.'),
                        ]),
                        p([
                            link('https://twitch.tv/silverjd', 'SilverJD'),
                            text(' played 10 '),
                            em('Mario Party'),
                            text(' games in a multi-day charity marathon for St. Jude. German streamer '),
                            link('https://twitch.tv/drache_proky', 'Drache_Proky'),
                            text(' spent August in a four-week subathon, running '),
                            em('Placid Plastic Duck Simulator'),
                            text(' overnight between party nights of '),
                            em('Pummel Party'),
                            text(', '),
                            em('Among Us'),
                            text(', and '),
                            em('Fall Guys'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Games',
                                'Observations',
                                'Avg Viewers',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/partychads', 'PARTYCHADS')],
                                    '17',
                                    '59',
                                    '17',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/alexfrets', 'AlexFrets')],
                                    '16',
                                    '30',
                                    '26',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/silverjd', 'SilverJD')],
                                    '10',
                                    '11',
                                    '10',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/drache_proky', 'Drache_Proky')],
                                    '9',
                                    '65',
                                    '27',
                                ],
                                [
                                    '5',
                                    [
                                        link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                    ],
                                    '8',
                                    '72',
                                    '86',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/scratchysn', 'ScratchYSN')],
                                    '8',
                                    '10',
                                    '35',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/pietsmiettv', 'PietSmietTV')],
                                    '8',
                                    '9',
                                    '216',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/jiizk', 'JIIZK')],
                                    '7',
                                    '40',
                                    '21',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/quarrysea', 'QuarrySea')],
                                    '7',
                                    '22',
                                    '54',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/pyroj_', 'PyroJ_')],
                                    '7',
                                    '14',
                                    '30',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'language-landscape',
            title: 'Language Landscape',
            collapsible: true,
            sections: [
                section({
                    id: 'language-landscape__languages-with-most-streams',
                    title: 'Languages with Most Streams',
                    collapsible: true,
                    blocks: [
                        p([
                            text('In July, '),
                            em('MECCHA CHAMELEON'),
                            text(' was the most-streamed game in the top 10 languages. In August it held that spot only in English, German, and Russian. The others went back to their usual favorites: '),
                            em('Among Us'),
                            text(' in Spanish, '),
                            em('nanos world'),
                            text(' in French, '),
                            em('Mahjong Soul'),
                            text(' in Japanese and Chinese, '),
                            em('Fall Guys'),
                            text(' in Italian, and '),
                            em('Diablo II: Resurrected'),
                            text(' in Polish. Portuguese crowned a new one: '),
                            em('Ragnarok Origin: Classic'),
                            text('.'),
                        ]),
                        p([
                            text('Most languages still ended August bigger than June, the month before the '),
                            em('MECCHA CHAMELEON'),
                            text(' surge. Portuguese streams were about 50% above June and German about a third. Chinese was the only large language below June, continuing a decline since spring.'),
                        ]),
                        p([
                            em('nanos world'),
                            text(' made up a quarter of all French streams, and the number of French streamers playing it rose from 136 in July to 203. Its biggest creator, '),
                            link('https://twitch.tv/mehditdonc', 'MehDitDonc'),
                            text(', drew 19% of all French viewers.'),
                        ]),
                        p([
                            text('German and Italian had the biggest average audiences, and one creator lifted each. '),
                            link('https://twitch.tv/tumblurr', 'Tumblurr'),
                            text("'s single "),
                            em('Super Battle Golf'),
                            text(' stream, at 44,533 viewers, brought 45% of all Italian viewers for the month. '),
                            link('https://twitch.tv/eliasn97', 'eliasn97'),
                            text(' brought 34% of German viewers with seven squad nights alongside friends such as '),
                            link('https://twitch.tv/xrohat', 'xRohat'),
                            text(', rotating through '),
                            em('MECCHA CHAMELEON'),
                            text(', '),
                            em('Among Us'),
                            text(', '),
                            em('Golf With Your Friends'),
                            text(', and '),
                            em('Pummel Party'),
                            text(". The squad's streams alone made up 57% of all "),
                            em('Golf With Your Friends'),
                            text(' viewers. Even without those two channels, German and Italian audiences stayed well above the overall average of 24.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Language',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'English', '116,192', '49,331', '19'],
                                ['2', 'Spanish', '17,107', '8,584', '13'],
                                ['3', 'French', '12,095', '4,349', '42'],
                                ['4', 'German', '11,601', '5,380', '59'],
                                ['5', 'Japanese', '10,121', '4,334', '20'],
                                ['6', 'Russian', '9,433', '4,574', '23'],
                                ['7', 'Portuguese', '6,784', '2,789', '25'],
                                ['8', 'Chinese', '2,800', '1,304', '27'],
                                ['9', 'Italian', '1,491', '713', '66'],
                                ['10', 'Polish', '1,181', '595', '35'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__english',
                    title: 'English',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__english__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'MECCHA CHAMELEON', '12,647', '9,345'],
                                        ['2', 'Super Smash Bros. Ultimate', '7,692', '2,191'],
                                        ['3', 'Fall Guys', '7,299', '3,588'],
                                        ['4', 'Among Us', '7,000', '4,120'],
                                        ['5', 'Words On Stream', '6,127', '502'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        [
                                            '1',
                                            'Salamander County Public Television',
                                            '1,778',
                                            '12',
                                        ],
                                        ['2', 'We, Junk Artists', '882', '16'],
                                        ['3', 'skribbl.io', '397', '171'],
                                        ['4', 'ANANTA', '387', '6'],
                                        ['5', 'Duck Race', '357', '7'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '210',
                                            '87',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '210',
                                            '11',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                            '208',
                                            '34',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                            '203',
                                            '20',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                                            ],
                                            '200',
                                            '29',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/jynxzi', 'Jynxzi')],
                                            '36,811',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/joe_bartolozzi', 'Joe_Bartolozzi'),
                                            ],
                                            '20,772',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/xqc', 'xQc')],
                                            '19,964',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/northernlion', 'Northernlion'),
                                            ],
                                            '11,940',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/angryginge13', 'angryginge13'),
                                            ],
                                            '7,333',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__spanish',
                    title: 'Spanish',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__spanish__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Among Us', '2,205', '1,373'],
                                        ['2', 'Fall Guys', '1,814', '1,032'],
                                        ['3', 'Super Smash Bros. Ultimate', '1,797', '602'],
                                        ['4', 'MECCHA CHAMELEON', '1,204', '975'],
                                        ['5', 'Gartic Phone', '1,011', '595'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Mario Party 3', '476', '12'],
                                        ['2', 'Wii Sports', '153', '33'],
                                        ['3', 'Elders K.O.', '135', '59'],
                                        ['4', 'An Eggstremely Hard Game', '100', '44'],
                                        ['5', 'Super Mario Strikers', '98', '24'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/grindilowtv', 'GrindilowTV')],
                                            '46',
                                            '29',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/tonimontanaatv', 'Tonimontanaatv'),
                                            ],
                                            '42',
                                            '17',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/venconbob', 'VenconBOB')],
                                            '42',
                                            '12',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/fu31go', 'Fu31go')],
                                            '31',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/vizard39', 'vizard39')],
                                            '31',
                                            '7',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/aldo_geo', 'aldo_geo')],
                                            '5,923',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/agustinunaplay8', 'AgustinUnaplay8'),
                                            ],
                                            '3,466',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/staryuuki', 'Staryuuki')],
                                            '2,495',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/paache_', 'paache_')],
                                            '2,127',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/elcamacho24', 'elcamacho24')],
                                            '2,115',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__french',
                    title: 'French',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__french__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'nanos world', '3,004', '203'],
                                        ['2', 'MECCHA CHAMELEON', '1,272', '934'],
                                        ['3', 'Among Us', '1,176', '623'],
                                        ['4', 'Fall Guys', '1,071', '442'],
                                        ['5', 'Super Smash Bros. Ultimate', '743', '256'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'MONOPOLY', '1,292', '11'],
                                        ['2', 'Maitre des Fleurs', '1,290', '17'],
                                        ['3', 'PICO PARK 2', '768', '13'],
                                        ['4', 'Lycans', '271', '150'],
                                        ['5', 'The Choicer Voicer', '255', '83'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/anas6424v1', 'anas6424v1')],
                                            '210',
                                            '5',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/miidouw_', 'Miidouw_')],
                                            '84',
                                            '96',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/shadowwgkd', 'ShadowwGKD')],
                                            '83',
                                            '63',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/heaven_et_alex', 'heaven_et_alex'),
                                            ],
                                            '65',
                                            '18',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/nacim_01', 'nacim_01')],
                                            '60',
                                            '71',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/ponce', 'Ponce')],
                                            '3,586',
                                            '14',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mehditdonc', 'MehDitDonc')],
                                            '2,967',
                                            '33',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/drfeelgood', 'DrFeelgood')],
                                            '1,662',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/littlebigwhale', 'LittleBigWhale'),
                                            ],
                                            '1,195',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/tayoh_', 'Tayoh_')],
                                            '952',
                                            '26',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__german',
                    title: 'German',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__german__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'MECCHA CHAMELEON', '2,580', '1,687'],
                                        ['2', 'Among Us', '1,750', '862'],
                                        ['3', 'Fall Guys', '769', '409'],
                                        ['4', 'Diablo II: Resurrected', '696', '101'],
                                        ['5', 'Golf With Your Friends', '654', '410'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Bow-wow Battle', '403', '5'],
                                        ['2', 'Pizza House Simulator', '267', '245'],
                                        ['3', 'The Choicer Voicer', '228', '317'],
                                        ['4', 'Among Us', '150', '1,750'],
                                        ['5', 'Pummel Party', '110', '309'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                            ],
                                            '72',
                                            '86',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/drache_proky', 'Drache_Proky'),
                                            ],
                                            '65',
                                            '27',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/yoonessa', 'YooNessa')],
                                            '62',
                                            '22',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/dolphde', 'DolphDE')],
                                            '33',
                                            '16',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/ohstoxictea', 'oHsToxicTea')],
                                            '31',
                                            '37',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                            '33,139',
                                            '7',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/trymacs', 'Trymacs')],
                                            '16,223',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/krokoboss', 'krokoboss')],
                                            '5,744',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/bonjwa', 'Bonjwa')],
                                            '5,602',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/xrohat', 'xRohat')],
                                            '5,499',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__japanese',
                    title: 'Japanese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__japanese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Mahjong Soul', '1,897', '685'],
                                        ['2', 'MECCHA CHAMELEON', '1,781', '1,395'],
                                        ['3', 'Super Smash Bros. Ultimate', '851', '221'],
                                        ['4', 'Beat Saber', '740', '155'],
                                        ['5', 'Among Us', '398', '282'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Jinsei Game for Nintendo Switch', '4,757', '7'],
                                        ['2', 'Sponge Break', '315', '8'],
                                        ['3', 'Deduce Together', '239', '18'],
                                        ['4', 'An Eggstremely Hard Game', '134', '35'],
                                        ['5', 'Super Smash Bros.', '133', '9'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/makosu', 'makosu')],
                                            '53',
                                            '20',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/kept_varrel', 'kept_VARREL')],
                                            '40',
                                            '144',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/pokapokacan', 'ぽかぽかさん')],
                                            '40',
                                            '35',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/hika_tin115', 'ひかちゃんねる'),
                                            ],
                                            '40',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/akakiks_', 'アカキクス')],
                                            '39',
                                            '39',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/jasper7se', 'Jasper7se')],
                                            '2,166',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/iq200yukaf', 'IQ200YukaF')],
                                            '1,918',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/ukyochi_jp', 'うきょちさん')],
                                            '1,885',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/titiseadmin', 'あどみん')],
                                            '570',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kettuncyukyuhei', 'けっつん')],
                                            '491',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__russian',
                    title: 'Russian',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__russian__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'MECCHA CHAMELEON', '1,847', '1,299'],
                                        ['2', 'Gartic Phone', '1,515', '754'],
                                        ['3', 'Jackbox Party Packs', '818', '297'],
                                        ['4', 'Diablo II: Resurrected', '810', '108'],
                                        ['5', 'SIGame', '545', '377'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Casino', '429', '21'],
                                        ['2', 'The Choicer Voicer', '138', '122'],
                                        ['3', 'Mario Kart World', '111', '13'],
                                        ['4', 'Rayman Raving Rabbids', '99', '5'],
                                        ['5', 'Goose Goose Duck', '60', '415'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/jackboxplay', 'jackboxplay')],
                                            '206',
                                            '46',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/kypced', 'kypced')],
                                            '80',
                                            '132',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/m19butcher', 'M19Butcher')],
                                            '51',
                                            '8',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/hc_fm', 'hc_fm')],
                                            '45',
                                            '14',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/bar_fly_one', 'bar_fly_one')],
                                            '35',
                                            '7',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/sleduck', 'SleDuck')],
                                            '4,760',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/orkpod', 'orkpod')],
                                            '4,304',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/yuuechka', 'yuuechka')],
                                            '3,044',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/dunduk', 'Dunduk')],
                                            '2,309',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/olesha', 'Olesha')],
                                            '2,222',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__portuguese',
                    title: 'Portuguese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__portuguese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Ragnarok Origin: Classic', '1,011', '129'],
                                        ['2', 'MECCHA CHAMELEON', '835', '513'],
                                        ['3', 'Words On Stream', '548', '48'],
                                        ['4', 'Fall Guys', '508', '160'],
                                        ['5', 'Among Us', '390', '248'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'The Choicer Voicer', '267', '101'],
                                        ['2', 'YARG', '215', '9'],
                                        ['3', 'Fruit Mountain', '117', '10'],
                                        ['4', 'Lycans', '99', '12'],
                                        ['5', 'Gartic on Stream', '86', '134'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/telinha', 'Telinha')],
                                            '210',
                                            '91',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/africaalbion', 'AfricaAlbion'),
                                            ],
                                            '94',
                                            '67',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/f0rsakenwow', 'f0rsakenwOw')],
                                            '92',
                                            '120',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/alduixd', 'AlduixD')],
                                            '71',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/trunksrx', 'Trunksrx')],
                                            '65',
                                            '16',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/boltz', 'boltz')],
                                            '1,662',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/tealzlol', 'Tealzlol')],
                                            '883',
                                            '8',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/bahiaqz', 'Bahiaqz')],
                                            '581',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/felps', 'Felps')],
                                            '482',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/choke7', 'Choke7')],
                                            '342',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__chinese',
                    title: 'Chinese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__chinese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Mahjong Soul', '571', '277'],
                                        ['2', 'MECCHA CHAMELEON', '523', '382'],
                                        ['3', 'Mabinogi', '294', '50'],
                                        ['4', 'Diablo II: Resurrected', '176', '34'],
                                        ['5', 'Party Animals', '152', '78'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Pizza House Simulator', '260', '66'],
                                        ['2', 'Heave Ho 2', '248', '7'],
                                        ['3', 'Trombone Champ', '243', '9'],
                                        ['4', 'Choppy Cuts', '49', '7'],
                                        ['5', 'We, Junk Artists', '38', '27'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/aldia1204', '艾爾狄亞')],
                                            '47',
                                            '9',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/daaafuuu', '草莓紅豆大福')],
                                            '35',
                                            '10',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/andyyeh88', '阿棠1214')],
                                            '21',
                                            '10',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/hakuteikuroya', '白帝黑也')],
                                            '21',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/elmilaw73', '快樂米米エルミミちゃん'),
                                            ],
                                            '20',
                                            '23',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mobilmobil', '老皮')],
                                            '5,149',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mimiluckying', '獅子丸')],
                                            '1,184',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/moondogs_celestial', '幻月_')],
                                            '1,138',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/jongie', '哈瓜哈瓜')],
                                            '862',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/reneesnowy', '蕾妮斯諾伊')],
                                            '344',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__italian',
                    title: 'Italian',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__italian__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Fall Guys', '269', '93'],
                                        ['2', 'Among Us', '251', '139'],
                                        ['3', 'Super Smash Bros. Ultimate', '112', '25'],
                                        ['4', 'MECCHA CHAMELEON', '86', '71'],
                                        ['5', 'Golf With Your Friends', '64', '51'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Super Battle Golf', '3,650', '14'],
                                        ['2', 'The Choicer Voicer', '355', '36'],
                                        ['3', 'An Eggstremely Hard Game', '244', '5'],
                                        ['4', 'Crash Bash', '191', '5'],
                                        ['5', 'Dino Party', '95', '6'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/pantofole94', 'pantofole94')],
                                            '45',
                                            '9',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/grimzally', 'GrimZally')],
                                            '21',
                                            '7',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/xraizo72', 'xRaizo72')],
                                            '20',
                                            '26',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/mondoeventgaming', 'mondoeventgaming'),
                                            ],
                                            '20',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/francesconigris', 'francesconigris'),
                                            ],
                                            '18',
                                            '157',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/therealmarzaa', 'TheRealMarzaa'),
                                            ],
                                            '5,558',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/lyonwgflive', 'LyonWGFLive')],
                                            '4,886',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/giankoextreme', 'giankoextreme'),
                                            ],
                                            '819',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kasumisen', 'KasumiSen')],
                                            '434',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/babbalucy', 'Babbalucy')],
                                            '250',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__polish',
                    title: 'Polish',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__polish__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Diablo II: Resurrected', '262', '43'],
                                        ['2', 'MECCHA CHAMELEON', '236', '188'],
                                        ['3', 'Fall Guys', '100', '39'],
                                        ['4', 'Among Us', '91', '54'],
                                        ['5', 'Gartic Phone', '51', '45'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__polish__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Gartic Phone', '253', '51'],
                                        ['2', 'The Choicer Voicer', '107', '28'],
                                        ['3', 'Pizza House Simulator', '74', '8'],
                                        ['4', 'Overcooked! 2', '69', '6'],
                                        ['5', 'Mario Kart World', '47', '12'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__polish__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/nadinwins', 'NadinWins')],
                                            '30',
                                            '161',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/iergo', 'iErGO')],
                                            '17',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/czechoooos', 'czechoooos')],
                                            '16',
                                            '21',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/lucy_sally', 'Lucy_Sally')],
                                            '12',
                                            '23',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/krzysiolek_tv', 'krzysiolek_tv'),
                                            ],
                                            '12',
                                            '10',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__polish__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/diables', 'Diables')],
                                            '1,201',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/xth0rek', 'xth0rek')],
                                            '844',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/rysiek', 'Rysiek')],
                                            '341',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/pevor13', 'peVoR13')],
                                            '268',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/xweza', 'xWeza')],
                                            '228',
                                            '4',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'game-deep-dives',
            title: 'Game Deep Dives',
            collapsible: true,
            sections: [
                section({
                    id: 'game-deep-dives__nanos-world',
                    title: 'nanos world',
                    collapsible: true,
                    blocks: [
                        p('3,152 streams | 215 streamers | 14.7 stickiness | 78 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__nanos-world__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/miidouw_', 'Miidouw_')],
                                            '84',
                                            '96',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/shadowwgkd', 'ShadowwGKD')],
                                            '83',
                                            '63',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/heaven_et_alex', 'heaven_et_alex'),
                                            ],
                                            '65',
                                            '18',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/nacim_01', 'nacim_01')],
                                            '60',
                                            '71',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/saketjr', 'SaketJR')],
                                            '58',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__nanos-world__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mehditdonc', 'MehDitDonc')],
                                            '2,967',
                                            '33',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/tayoh_', 'Tayoh_')],
                                            '952',
                                            '26',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/khadim', 'Khadim')],
                                            '668',
                                            '17',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/fafa', 'Fafa')],
                                            '464',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/mowraa', 'Mowraa')],
                                            '395',
                                            '31',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__diablo-ii-resurrected',
                    title: 'Diablo II: Resurrected',
                    collapsible: true,
                    blocks: [
                        p('8,320 streams | 1,301 streamers | 6.4 stickiness | 21 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__diablo-ii-resurrected__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '210',
                                            '87',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/kahplunk', 'Kahplunk')],
                                            '84',
                                            '11',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/kypced', 'kypced')],
                                            '80',
                                            '132',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/generalbardock', 'GeneralBardock'),
                                            ],
                                            '61',
                                            '20',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/m19butcher', 'M19Butcher')],
                                            '51',
                                            '8',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__diablo-ii-resurrected__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mathil1', 'Mathil1')],
                                            '830',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mrllamasc', 'MrLlamaSC')],
                                            '588',
                                            '40',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/kano', 'Kano')],
                                            '404',
                                            '45',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/empyriangaming', 'Empyriangaming'),
                                            ],
                                            '404',
                                            '14',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/vitablo', 'VITABLO')],
                                            '379',
                                            '9',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__super-smash-bros-ultimate',
                    title: 'Super Smash Bros. Ultimate',
                    collapsible: true,
                    blocks: [
                        p('11,817 streams | 3,497 streamers | 3.4 stickiness | 20 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__super-smash-bros-ultimate__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                                            ],
                                            '200',
                                            '29',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/metalvayne08', 'metalvayne08'),
                                            ],
                                            '80',
                                            '58',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/akakiks_', 'アカキクス')],
                                            '37',
                                            '39',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/flip_fantasia', 'Flip_Fantasia'),
                                            ],
                                            '35',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kept_varrel', 'kept_VARREL')],
                                            '34',
                                            '149',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__super-smash-bros-ultimate__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/zentreya', 'Zentreya')],
                                            '3,620',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/vgbootcamp', 'VGBootCamp')],
                                            '1,736',
                                            '33',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/vgbootcamp3', 'vgbootcamp3')],
                                            '899',
                                            '12',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/hungrybox', 'Hungrybox')],
                                            '678',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/fatalityfalcon', 'FatalityFalcon'),
                                            ],
                                            '538',
                                            '4',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__words-on-stream',
                    title: 'Words On Stream',
                    collapsible: true,
                    blocks: [
                        p('7,161 streams | 594 streamers | 12.1 stickiness | 16 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__words-on-stream__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '210',
                                            '11',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                            '208',
                                            '34',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                            '203',
                                            '20',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/the_coop_couchcrew', 'the_coop_couchcrew'),
                                            ],
                                            '148',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/splatpoyo', 'SplatPoyo')],
                                            '123',
                                            '11',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__words-on-stream__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/piapiufo', 'PiaPiUFO')],
                                            '596',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/whoischelsea', 'whoischelsea'),
                                            ],
                                            '190',
                                            '6',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/cochard', 'cochard')],
                                            '184',
                                            '45',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/eeiris', 'Eeiris')],
                                            '161',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/x33n', 'X33N')],
                                            '155',
                                            '109',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__ragnarok-origin-classic',
                    title: 'Ragnarok Origin: Classic',
                    collapsible: true,
                    blocks: [
                        p('1,562 streams | 201 streamers | 7.8 stickiness | 21 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__ragnarok-origin-classic__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/alduixd', 'AlduixD')],
                                            '71',
                                            '9',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/trunksrx', 'Trunksrx')],
                                            '65',
                                            '16',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/pain_', 'PAIN_')],
                                            '52',
                                            '15',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/grindilowtv', 'GrindilowTV')],
                                            '46',
                                            '29',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/phwasd', 'phwasd')],
                                            '38',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__ragnarok-origin-classic__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/tealzlol', 'Tealzlol')],
                                            '883',
                                            '8',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/ouluh', 'OuluH')],
                                            '204',
                                            '23',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/jojotshy', 'jojotshy')],
                                            '142',
                                            '17',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/cahos_gaming', 'CAHOS_GAMING'),
                                            ],
                                            '142',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/zinaofc', 'zinaofc')],
                                            '102',
                                            '4',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__just-dance-2026-edition',
                    title: 'Just Dance 2026 Edition',
                    collapsible: true,
                    blocks: [
                        p('495 streams | 197 streamers | 2.5 stickiness | 47 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__just-dance-2026-edition__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/remeladegatos', 'remeladegatos'),
                                            ],
                                            '23',
                                            '5',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/sierravampire', 'SierraVampire'),
                                            ],
                                            '22',
                                            '65',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/junehh_whitestar', 'junehh_whitestar'),
                                            ],
                                            '13',
                                            '17',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/ulises_eric', 'Ulises_Eric')],
                                            '12',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/dancewithk', 'dancewithk')],
                                            '11',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__just-dance-2026-edition__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/bebahan', 'Bebahan')],
                                            '1,383',
                                            '5',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/sophelyy', 'Sophelyy')],
                                            '191',
                                            '9',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/sincerelylyn', 'sincerelyLyn'),
                                            ],
                                            '191',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/arielehm', 'Arielehm')],
                                            '97',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/arianakatana', 'ArianaKatana'),
                                            ],
                                            '93',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__pinball',
                    title: 'Pinball',
                    collapsible: true,
                    blocks: [
                        p('674 streams | 160 streamers | 4.2 stickiness | 26 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__pinball__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/steelvalleypinball', 'SteelValleyPinball'),
                                            ],
                                            '23',
                                            '45',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/foxcitiespinball', 'Foxcitiespinball'),
                                            ],
                                            '21',
                                            '271',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/enteryourinitials', 'EnterYourInitials'),
                                            ],
                                            '14',
                                            '32',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/flipsidememphis', 'flipsidememphis'),
                                            ],
                                            '13',
                                            '33',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/batcavepinball', 'batcavepinball'),
                                            ],
                                            '13',
                                            '29',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__pinball__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/foxcitiespinball', 'Foxcitiespinball'),
                                            ],
                                            '271',
                                            '21',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/geekgamertv', 'GeekGamerTV')],
                                            '151',
                                            '12',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/dri374', 'DRI374')],
                                            '70',
                                            '9',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/spspinball', 'SPSPINBALL')],
                                            '68',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/lizstar', 'Lizstar')],
                                            '54',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__gartic-on-stream',
                    title: 'Gartic on Stream',
                    collapsible: true,
                    blocks: [
                        p('266 streams | 43 streamers | 6.2 stickiness | 55 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__gartic-on-stream__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/f0rsakenwow', 'f0rsakenwOw')],
                                            '90',
                                            '119',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/cpxtrey', 'CPxTrey')],
                                            '57',
                                            '43',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/luizeaguilar', 'LuizeAguilar'),
                                            ],
                                            '13',
                                            '14',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/ygvexxi', 'YGVexxi')],
                                            '7',
                                            '14',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/makeupmommy88', 'makeupmommy88'),
                                            ],
                                            '7',
                                            '7',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__gartic-on-stream__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/f0rsakenwow', 'f0rsakenwOw')],
                                            '119',
                                            '90',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/isaroza_', 'isaroza_')],
                                            '119',
                                            '4',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/cpxtrey', 'CPxTrey')],
                                            '43',
                                            '57',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/thor_q', 'Thor_Q')],
                                            '29',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/pinebranch', 'PineBranch')],
                                            '26',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__jackbox-party-packs',
                    title: 'Jackbox Party Packs',
                    collapsible: true,
                    blocks: [
                        p('3,991 streams | 1,993 streamers | 2 stickiness | 31 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__jackbox-party-packs__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/jackboxplay', 'jackboxplay')],
                                            '206',
                                            '46',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/youplaylive', 'youplaylive')],
                                            '196',
                                            '7',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/chaotik_lord', 'chaotik_lord'),
                                            ],
                                            '47',
                                            '9',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/mattitude1233', 'Mattitude1233'),
                                            ],
                                            '27',
                                            '21',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/andojeebo', 'AndoJeebo')],
                                            '25',
                                            '21',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__jackbox-party-packs__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/dooleynoted', 'DooleyNoted')],
                                            '1,229',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/hcjustin', 'HCJustin')],
                                            '1,004',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/skaifox', 'Skaifox')],
                                            '705',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/dumbdog', 'DumbDog')],
                                            '496',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/swiftor', 'Swiftor')],
                                            '367',
                                            '4',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__super-mario-party-jamboree',
                    title: 'Super Mario Party Jamboree',
                    collapsible: true,
                    blocks: [
                        p('1,243 streams | 620 streamers | 2 stickiness | 44 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__super-mario-party-jamboree__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/wardere', 'WARDERE')],
                                            '24',
                                            '38',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/iamkoreanjj', 'IamKoreanJJ')],
                                            '21',
                                            '8',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/norangard', 'Norangard')],
                                            '7',
                                            '8',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/sobeeze', 'Sobeeze')],
                                            '6',
                                            '12',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/cliff_c718', 'Cliff_C718')],
                                            '6',
                                            '9',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__super-mario-party-jamboree__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/squeex', 'Squeex')],
                                            '6,722',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/valkyrae', 'Valkyrae')],
                                            '5,856',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/fuslie', 'fuslie')],
                                            '3,302',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/foolish', 'Foolish')],
                                            '2,086',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/porcelainmaid', 'PorcelainMaid'),
                                            ],
                                            '728',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streaming-patterns',
            title: 'Streaming Patterns',
            collapsible: true,
            sections: [
                section({
                    id: 'streaming-patterns__streaming-patterns-by-day-of-week',
                    title: 'Streaming Patterns by Day of Week',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Weekends again had more streams but smaller audiences. From Friday to Sunday, about 1,050 streams were live at a time, averaging about 20 viewers each. From Monday to Thursday, about 810 were live, averaging about 28. Thursday had the biggest audiences of the week, and '),
                            link('https://twitch.tv/eliasn97', 'eliasn97'),
                            text("'s squad nights drew 17% of all Thursday viewers."),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Day', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['Sunday', '1,076', '21'],
                                ['Monday', '806', '23'],
                                ['Tuesday', '795', '30'],
                                ['Wednesday', '808', '24'],
                                ['Thursday', '817', '34'],
                                ['Friday', '949', '21'],
                                ['Saturday', '1,133', '19'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streaming-patterns__streaming-patterns-by-hour-of-day',
                    title: 'Streaming Patterns by Hour of Day',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Audiences peaked from 17:00 to 21:00 UTC, daytime in the Americas and evening in Europe. Explore August hourly activity in more detail using the '),
                            link('/party-central?visualization=heatmap&measure=avgStreamsPerCapture&x=hourUtc&y=dayOfWeekUtc&grain=&from=2026-08-01T00%3A00%3A00.000Z&to=2026-09-01T00%3A00%3A00.000Z', 'Party Central data explorer'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Hour (UTC)', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['00:00', '1,289', '17'],
                                ['01:00', '1,338', '20'],
                                ['02:00', '1,340', '20'],
                                ['03:00', '1,415', '17'],
                                ['04:00', '994', '17'],
                                ['05:00', '751', '23'],
                                ['06:00', '695', '13'],
                                ['07:00', '531', '13'],
                                ['08:00', '452', '17'],
                                ['09:00', '394', '17'],
                                ['10:00', '419', '17'],
                                ['11:00', '446', '24'],
                                ['12:00', '589', '19'],
                                ['13:00', '754', '20'],
                                ['14:00', '788', '21'],
                                ['15:00', '769', '20'],
                                ['16:00', '829', '18'],
                                ['17:00', '987', '28'],
                                ['18:00', '1,144', '35'],
                                ['19:00', '1,248', '36'],
                                ['20:00', '1,294', '34'],
                                ['21:00', '1,158', '37'],
                                ['22:00', '1,121', '29'],
                                ['23:00', '1,255', '18'],
                            ],
                        }),
                    ],
                }),
            ],
        }),
    ],
});
//# sourceMappingURL=2026-08-top-party-games-and-streamers.js.map